import { Coordinates, CropMatchResult, DocFieldRaw, DocScanField, FarmFieldRaw } from "models";

import { getDocFieldCrops, getDocFieldValues } from "components";

export type SectionRow = {
  type: "section";
  key: string;
  label: string;
  status?: string;
  score?: number;
};

export type DataRow = {
  type: "data";
  key: string;
  item: string;
  dbValue: string;
  docValue: string;
  status?: string;
  score?: number;
};

export type MapRow = {
  type: "map";
  key: string;
  platformGeometry?: Coordinates[];
  docGeometries?: Coordinates[][];
  status?: string | null;
  score?: number | null;
  onOpen: () => void;
};

export type ComparisonRow = SectionRow | DataRow | MapRow;

const joinDocFieldValues = (docField: DocFieldRaw | undefined, pick: (field: DocFieldRaw) => string | null | undefined) =>
  getDocFieldValues(docField, pick).join(", ") || "-";

/** A grouped doc field has one size per parcel, so the recorded size difference is shown only for a single field. */
export const getDocFieldSizeLabel = (docField: DocFieldRaw | undefined, sizeDifference: { db: string } | undefined): string => {
  const sizes = getDocFieldValues(docField, (field) => field.field_size);
  if (sizes.length === 0) return "-";
  const sizeLabel = sizes.map((size) => `${size} ha`).join(", ");
  if (!sizeDifference || sizes.length > 1) return sizeLabel;
  return `${sizeLabel} (was ${sizeDifference.db} ha)`;
};

export function buildFieldRows(
  dbField: FarmFieldRaw | undefined,
  docField: DocFieldRaw | undefined,
  scores: DocScanField["scores"],
  statuses: DocScanField["statuses"],
  docFieldSizeLabel: string
): ComparisonRow[] {
  return [
    {
      type: "data",
      key: "name",
      item: "Name",
      dbValue: dbField?.name ?? "-",
      docValue: joinDocFieldValues(docField, (field) => field.field_name),
      status: statuses?.field_name_status,
      score: scores?.field_name_score,
    },
    {
      type: "data",
      key: "number",
      item: "Number",
      dbValue: dbField?.number ?? "-",
      docValue: joinDocFieldValues(docField, (field) => field.field_number),
      status: statuses?.field_number_status,
      score: scores?.field_number_score,
    },
    {
      type: "data",
      key: "flik",
      item: "FLIK",
      dbValue: dbField?.flik ?? "-",
      docValue: joinDocFieldValues(docField, (field) => field.flik_number),
      status: statuses?.flik_status,
      score: scores?.flik_score,
    },
    {
      type: "data",
      key: "size",
      item: "Size",
      dbValue: dbField?.size !== undefined ? `${dbField.size} ha` : "-",
      docValue: docFieldSizeLabel,
      status: statuses?.field_size_status,
      score: scores?.field_size_score,
    },
  ];
}

export function buildCropRows(
  docField: DocFieldRaw | undefined,
  dbField: FarmFieldRaw | undefined,
  fieldCrops: CropMatchResult[],
  cropsScore: number | undefined,
  cropsStatus: string | undefined
): ComparisonRow[] {
  // A group repeats each crop once per parcel; listing it once keeps rows aligned with the platform crops.
  const docCrops = getDocFieldCrops(docField);
  if (docCrops.length === 0) return [];

  const rows: ComparisonRow[] = [{ type: "section", key: "crops-section", label: "Crops", status: cropsStatus, score: cropsScore }];

  const dbCrops = dbField?.crops ?? [];

  docCrops.forEach((docCrop, idx) => {
    const match = fieldCrops[idx];
    const dbCrop = dbCrops[idx];

    rows.push({
      type: "data",
      key: `crop-${idx}-name`,
      item: "Name",
      dbValue: dbCrop?.crop_name ?? "-",
      docValue: docCrop.crop_name,
      status: match?.crop_name_status ?? undefined,
      score: match?.crop_name_score ?? undefined,
    });

    rows.push({
      type: "data",
      key: `crop-${idx}-year`,
      item: "Year",
      dbValue: dbCrop != null ? String(dbCrop.year) : "-",
      docValue: String(docCrop.crop_year),
      status: match?.crop_year_status ?? undefined,
      score: match?.crop_year_score ?? undefined,
    });
  });

  return rows;
}
